// frontend/src/pages/SuperAdminDashboard.jsx
// ============================================================================
// SUPER-ADMIN DASHBOARD v4 — ENTERPRISE COMMAND CENTER
// Light/Dark mode · HoraPilot brand (#448696) · Sidebar · Shared ThemeContext
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Building2, Users, AlertCircle, Plus, Search,
  RefreshCw, LogOut, Crown, TrendingUp, Activity,
  Bell, ChevronRight, Zap, Database, ChevronDown,
  CheckCircle2, LayoutDashboard, Shield,
  ArrowUpRight, ArrowDownRight, Minus, Sun, Moon,
  Clock, Server, FileText, Calendar, Settings2, Send, FileCheck,
  Info, ShieldAlert, Activity as ActivityIcon, ClipboardCheck, LineChart,
  Timer, UserCog
} from 'lucide-react';
import superAdminApi from '../services/superAdminApi';
import CompanyList from '../components/admin/CompanyList';
import CompanyForm from '../components/admin/CompanyForm';
import LicenceManagementModal from '../components/admin/LicenceManagementModal';
import FeatureFlags from '../components/admin/FeatureFlags';
import { useNavigate } from 'react-router-dom';
import { ThemeContext, THEMES, useTheme } from '../contexts/ThemeContext';
import LoginConfigManager from '../components/admin/LoginConfigManager';
import CommunicationsAdmin from '../components/admin/CommunicationsAdmin';
import BillingConfig from '../components/admin/BillingConfig';
import CronManager from '../components/admin/CronManager';
import InvoicesManager from '../components/admin/InvoicesManager';
import DanicedSettings from '../components/admin/DanicedSettings';
import ProspectionManager from '../components/admin/ProspectionManager';
import ContratManager from '../components/admin/ContratManager';
import DevisManager from '../components/admin/DevisManager';
import ProspectsManager from '../components/admin/ProspectsManager';
import LicenceManager from '../components/admin/LicenceManager';
import ExtensionsManager from '../components/admin/ExtensionsManager';
import LicenceModulesManager from '../components/admin/LicenceModulesManager';
import ProspectionHub from '../components/admin/ProspectionHub';
import VersionManager from '../components/admin/VersionManager';
import LoginAttempts from '../components/admin/LoginAttempts';
import ActionsPrivilegiees from '../components/admin/ActionsPrivilegiees';
import Exploitation from '../components/admin/Exploitation';
import Conformite from '../components/admin/Conformite';
import Activite from '../components/admin/Activite';
import RequetesLentes from '../components/admin/RequetesLentes';
import Administrateurs from '../components/admin/Administrateurs';


// ============================================================================
// NAVIGATION
// ============================================================================


{/*const NAV_SECTIONS = [
  {
    title: 'Supervision',
    items: [
      { key: 'overview',  label: 'Tableau de bord', Icon: LayoutDashboard },
      { key: 'companies', label: 'Entreprises',     Icon: Building2 },
      { key: 'prospection', label: 'Prospection', Icon: Send },
      { key: 'contrats', label: 'Contrats', Icon: FileCheck },
      { key: 'devis', label: 'Devis', Icon: FileText },
      { key: 'prospects', label: 'Prospects', Icon: Users },
      { key: 'licences', label: 'Licences', Icon: Shield },
      { key: 'extensions', label: 'Extensions', Icon: Zap},
      { key: 'licencesModule', label: 'Licence Module', Icon: Shield}
    ]
  },
  {
    title: 'Configuration',
    items: [
      { key: 'features', label: 'Feature Flags', Icon: Zap },
      { key: 'login_config', label: 'Page de connexion', Icon: FileText },
      { key: 'communications', label: 'Communications', Icon: Bell },
      { key: 'billing', label: 'Facturation', Icon: Calendar },
      { key: 'cron', label: 'Planification', Icon: Clock },
      { key: 'invoices', label: 'Factures', Icon: FileText },
      { key: 'daniced-settings', label: 'Nos parametres', Icon: Settings2 },
    ]
  },
]; */}


const NAV_SECTIONS = [
  {
    title: 'Prospection',
    items: [
      { key: 'prospection_hub', label: 'Pipeline',    Icon: Send },
      { key: 'devis',           label: 'Devis',       Icon: FileText },
      { key: 'contrats',        label: 'Contrats',    Icon: FileCheck },
      { key: 'prospection', label: 'Prospection', Icon: Send },
    ]
  },
  {
    title: 'Supervision',
    items: [
      { key: 'overview',        label: 'Tableau de bord', Icon: LayoutDashboard },
      { key: 'companies',       label: 'Entreprises',     Icon: Building2 },
      { key: 'connexions',      label: 'Connexions',      Icon: ShieldAlert },
      { key: 'activite',        label: 'Activité',        Icon: LineChart },
      { key: 'requetes',        label: 'Requêtes lentes', Icon: Timer },
      { key: 'licences',        label: 'Licences',        Icon: Shield },
      { key: 'extensions',      label: 'Extensions',      Icon: Zap },
      { key: 'licencesModule',  label: 'Modules',         Icon: Shield },
    ]
  },
  {
    title: 'Contrôle',
    items: [
      { key: 'actions',         label: 'Actions privilégiées', Icon: ShieldAlert },
      { key: 'conformite',      label: 'Conformité',      Icon: ClipboardCheck },
      { key: 'exploitation',    label: 'Exploitation',    Icon: ActivityIcon },
      { key: 'administrateurs', label: 'Administrateurs', Icon: UserCog },
    ]
  },
  {
    title: 'Configuration',
    items: [
      { key: 'features',        label: 'Feature Flags',   Icon: Zap },
      { key: 'login_config',    label: 'Page connexion',  Icon: FileText },
      { key: 'communications',  label: 'Communications',  Icon: Bell },
      { key: 'billing',         label: 'Facturation',     Icon: Calendar },
      { key: 'cron',            label: 'Planification',   Icon: Clock },
      { key: 'invoices',        label: 'Factures',        Icon: FileText },
      { key: 'daniced-settings',label: 'Nos paramètres',  Icon: Settings2 },
      { key: 'VersionManager',label: 'Version Horapilot',  Icon: Info },

    ]
  },
];

// ============================================================================
// StatusDot
// ============================================================================
const StatusDot = ({ status, blocked }) => {
  const colors = { active: 'bg-emerald-400', trial: 'bg-amber-400', suspended: 'bg-gray-400' };
  if (blocked) return <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />;
  return <span className={`w-2 h-2 rounded-full inline-block ${colors[status] || 'bg-gray-400'}`} />;
};

// ============================================================================
// KPITile
// ============================================================================
const KPITile = ({ label, value, sub, alert }) => {
  const t = useTheme();
  return (
    <div
      className="relative px-5 py-4 rounded-xl border transition-all duration-150 group min-w-0 overflow-hidden"
      style={{
        backgroundColor: alert ? t.dangerSoft : t.bgCard,
        borderColor: alert ? t.dangerText + '30' : t.border,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = alert ? t.dangerSoft : t.bgCardHover;
        e.currentTarget.style.borderColor = alert ? t.dangerText + '50' : t.borderActive;
        e.currentTarget.style.boxShadow = t.shadowLg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = alert ? t.dangerSoft : t.bgCard;
        e.currentTarget.style.borderColor = alert ? t.dangerText + '30' : t.border;
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[2px] rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ backgroundColor: alert ? t.dangerText : t.primary }}
      />
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] mb-2" style={{ color: t.textMuted }}>
        {label}
      </div>
      <div className="flex items-end gap-2 min-w-0">
        <span className="text-[28px] font-extrabold leading-none tracking-tight tabular-nums" style={{ color: alert ? t.dangerText : t.text }}>
          {value}
        </span>
        {sub && <span className="text-xs font-medium mb-0.5 truncate" style={{ color: t.textMuted }}>{sub}</span>}
      </div>
    </div>
  );
};

// ============================================================================
// SystemStatusBar
// ============================================================================
const SystemStatusBar = ({ stats, onRefresh, refreshing, isDark, onToggleTheme }) => {
  const t = useTheme();
  const now = new Date();
  const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const dateStr = now.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  const healthPct = stats?.totalCompanies ? Math.round((stats.activeCompanies / stats.totalCompanies) * 100) : 0;

  return (
    <div className="flex items-center gap-4 text-xs" style={{ color: t.textMuted }}>
      <div className="flex items-center gap-1.5">
        <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: t.success }} />
        <span className="font-medium" style={{ color: t.textSecondary }}>Opérationnel</span>
      </div>
      <span style={{ color: t.borderLight }}>·</span>
      <div className="flex items-center gap-1.5">
        <Server size={12} />
        <span className="font-mono">{healthPct}%</span>
      </div>
      <span style={{ color: t.borderLight }}>·</span>
      <div className="flex items-center gap-1.5">
        <Clock size={12} />
        <span className="font-mono capitalize">{dateStr} · {timeStr}</span>
      </div>

      <div className="ml-auto flex items-center gap-1">
        <button onClick={onRefresh} disabled={refreshing}
          className="p-1.5 rounded-md transition-colors disabled:opacity-40" style={{ color: t.textMuted }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.primarySoft}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          title="Actualiser"
        >
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
        </button>
        <button className="p-1.5 rounded-md transition-colors relative" style={{ color: t.textMuted }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.primarySoft}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          title="Notifications"
        >
          <Bell size={13} />
          {stats?.blockedCompanies > 0 && (
            <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full" style={{ backgroundColor: t.danger }} />
          )}
        </button>
        <div className="w-px h-4 mx-1" style={{ backgroundColor: t.border }} />
        <button onClick={onToggleTheme} className="p-1.5 rounded-md transition-all" style={{ color: t.primary }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.primarySoft}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          title={isDark ? 'Mode jour' : 'Mode nuit'}
        >
          {isDark ? <Sun size={14} /> : <Moon size={14} />}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// QuickStats
// ============================================================================
const QuickStats = ({ stats }) => {
  const t = useTheme();
  const items = [
    { label: 'TOTAL',    value: stats?.totalCompanies || 0,  color: t.text },
    { label: 'ACTIVES',  value: stats?.activeCompanies || 0, color: t.successText },
    { label: 'ESSAI',    value: stats?.trialCompanies || 0,  color: t.warningText },
    { label: 'BLOQUÉES', value: stats?.blockedCompanies || 0, color: stats?.blockedCompanies > 0 ? t.dangerText : t.textMuted },
    { label: 'USERS',    value: stats?.totalUsers || 0,       color: t.primaryText },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 rounded-lg border overflow-hidden"
      style={{ backgroundColor: t.bgCard, borderColor: t.border }}>
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {i > 0 && <div className="w-px h-4 hidden sm:block" style={{ backgroundColor: t.border }} />}
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] whitespace-nowrap" style={{ color: t.textMuted }}>{item.label}</span>
            <span className="text-sm font-bold tabular-nums" style={{ color: item.color }}>{item.value}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

// ============================================================================
// MiniTable
// ============================================================================
const MiniTable = ({ companies, onViewAll }) => {
  const t = useTheme();
  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: t.border }}>
      <div className="grid grid-cols-[1fr_100px_80px_60px] gap-2 px-4 py-2.5 border-b"
        style={{ backgroundColor: t.bgTableHeader, borderColor: t.border }}>
        {['Entreprise', 'Slug', 'Statut', 'Users'].map((h, i) => (
          <span key={h} className={`text-[10px] font-bold uppercase tracking-[0.14em] truncate ${i === 3 ? 'text-right' : ''}`} style={{ color: t.textMuted }}>{h}</span>
        ))}
      </div>
      {companies.slice(0, 8).map((c, i) => (
        <div key={c.id || i}
          className="grid grid-cols-[1fr_100px_80px_60px] gap-2 px-4 py-2.5 border-b transition-colors cursor-default"
          style={{ borderColor: t.borderLight }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.bgTableRowHover}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <StatusDot status={c.status} blocked={c.is_blocked} />
            <span className="text-sm font-medium truncate" style={{ color: t.text }}>{c.company_name}</span>
          </div>
          <span className="text-sm font-mono truncate" style={{ color: t.textMuted }}>{c.company_slug}</span>
          <span className="text-xs font-semibold capitalize" style={{
            color: c.is_blocked ? t.dangerText : c.status === 'active' ? t.successText : c.status === 'trial' ? t.warningText : t.textMuted
          }}>
            {c.is_blocked ? 'Bloquée' : c.status}
          </span>
          <span className="text-sm font-mono text-right tabular-nums" style={{ color: t.textSecondary }}>
            {c.user_count || c.totalUsers || '—'}
          </span>
        </div>
      ))}
      <button onClick={onViewAll}
        className="w-full px-4 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
        style={{ color: t.primaryText }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.primarySofter}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        Voir toutes les entreprises
        <ChevronRight size={13} />
      </button>
    </div>
  );
};

// ============================================================================
// DistributionPanel
// ============================================================================
const DistributionPanel = ({ stats }) => {
  const t = useTheme();
  const total = stats?.totalCompanies || 1;
  const bars = [
    { label: 'Actives',  value: stats?.activeCompanies || 0,  color: t.successText },
    { label: 'Essai',    value: stats?.trialCompanies || 0,   color: t.warningText },
    { label: 'Bloquées', value: stats?.blockedCompanies || 0, color: t.dangerText },
  ];
  return (
    <div className="rounded-xl border p-5" style={{ backgroundColor: t.bgCard, borderColor: t.border }}>
      <h4 className="text-[11px] font-bold uppercase tracking-[0.14em] mb-4" style={{ color: t.textMuted }}>Répartition</h4>
      <div className="space-y-3.5">
        {bars.map(item => (
          <div key={item.label}>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-medium" style={{ color: t.textSecondary }}>{item.label}</span>
              <span className="font-bold tabular-nums" style={{ color: t.text }}>{item.value}</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: t.primarySofter }}>
              <div className="h-full rounded-full transition-all duration-700 ease-out" style={{
                width: `${Math.max((item.value / total) * 100, item.value > 0 ? 4 : 0)}%`,
                backgroundColor: item.color, opacity: 0.75,
              }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const SuperAdminDashboard = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeView, setActiveView] = useState('overview');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showLicenceModal, setShowLicenceModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [initialContratId, setInitialContratId] = useState(null);

  const [initialProspectId,  setInitialProspectId]  = useState(null);

  // Theme
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('hp-admin-theme') === 'dark'; } catch { return false; }
  });
  const theme = isDark ? THEMES.dark : THEMES.light;
  const toggleTheme = useCallback(() => {
    setIsDark(prev => {
      const next = !prev;
      try { localStorage.setItem('hp-admin-theme', next ? 'dark' : 'light'); } catch {}
      return next;
    });
  }, []);

  // Data
  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const [statsRes, companiesRes] = await Promise.all([
        superAdminApi.stats.getGlobalStats(),
        superAdminApi.companies.getAll()
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data || []);
    } catch (err) {
      console.error('[AdminCenter] Load error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);


  // useEffect pour reset initialContratId quand on quitte les contrats
  useEffect(() => {
    if (activeView !== 'contrats') setInitialContratId(null);
  }, [activeView]);
  
  useEffect(() => {
    if (activeView !== 'devis') setInitialProspectId(null);
  }, [activeView]);
  

  const filteredCompanies = useMemo(() => {
    let r = companies;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      r = r.filter(c => c.company_name?.toLowerCase().includes(q) || c.company_slug?.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      r = statusFilter === 'blocked' ? r.filter(c => c.is_blocked) : r.filter(c => !c.is_blocked && c.status === statusFilter);
    }
    return r;
  }, [companies, searchTerm, statusFilter]);

  // Handlers
  const handleCompanyCreated = useCallback(() => { setShowCreateForm(false); loadData(true); }, [loadData]);
  const handleCompanyUpdated = useCallback(() => loadData(true), [loadData]);
  const handleManageLicence = useCallback((c) => { setSelectedCompany(c); setShowLicenceModal(true); }, []);
  const handleLicenceUpdated = useCallback(() => { setShowLicenceModal(false); setSelectedCompany(null); loadData(true); }, [loadData]);
  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  }, [navigate]);

  // Shortcut
  const t = theme;

  // ==========================================================================
  // LOADING
  // ==========================================================================
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: t.bg }}>
        <div className="text-center">
          <div className="relative w-14 h-14 mx-auto mb-5">
            <div className="absolute inset-0 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: t.border, borderTopColor: t.primary }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <Shield size={18} style={{ color: t.primary }} />
            </div>
          </div>
          <p className="text-sm font-semibold" style={{ color: t.textSecondary }}>Initialisation du système</p>
          <div className="mt-3 flex items-center justify-center gap-1">
            {[0, 200, 400].map(d => (
              <span key={d} className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: t.primary, animationDelay: `${d}ms` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <ThemeContext.Provider value={theme}>
      <div className="min-h-screen flex transition-colors duration-300" style={{ backgroundColor: t.bg, color: t.text }}>

        {/* ============================================================== */}
        {/* SIDEBAR                                                        */}
        {/* ============================================================== */}
        <aside className={`${sidebarCollapsed ? 'w-[60px]' : 'w-[230px]'} flex-shrink-0 flex flex-col border-r transition-all duration-200 ease-out sticky top-0 h-screen z-30`}
          style={{ backgroundColor: t.bgSidebar, borderColor: t.border }}>

          {/* Brand */}
          <div className="p-3.5 border-b" style={{ borderColor: t.border }}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.primaryDark})` }}>
                <Crown size={15} className="text-white" />
              </div>
              {!sidebarCollapsed && (
                <div className="min-w-0">
                  <div className="text-[13px] font-bold tracking-tight" style={{ color: t.text }}>HORAPILOT</div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: t.textMuted }}>Centre d'installation</div>
                </div>
              )}
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 py-4 px-2 space-y-5 overflow-y-auto">
            {NAV_SECTIONS.map(section => (
              <div key={section.title}>
                {!sidebarCollapsed && (
                  <div className="px-2.5 mb-2 text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: t.textMuted }}>
                    {section.title}
                  </div>
                )}
                <div className="space-y-0.5">
                  {section.items.map(({ key, label, Icon }) => {
                    const active = activeView === key;
                    return (
                      <button key={key} onClick={() => setActiveView(key)}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-100 border"
                        style={{
                          backgroundColor: active ? t.primarySoft : 'transparent',
                          borderColor: active ? t.primaryBorder : 'transparent',
                          color: active ? t.primaryText : t.textSecondary,
                        }}
                        onMouseEnter={(e) => { if (!active) { e.currentTarget.style.backgroundColor = t.primarySofter; e.currentTarget.style.color = t.text; }}}
                        onMouseLeave={(e) => { if (!active) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = t.textSecondary; }}}
                        title={sidebarCollapsed ? label : undefined}
                      >
                        <Icon size={16} strokeWidth={active ? 2.2 : 1.8} className="flex-shrink-0" />
                        {!sidebarCollapsed && <span className="truncate">{label}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-2.5 border-t space-y-0.5" style={{ borderColor: t.border }}>
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] font-medium transition-colors"
              style={{ color: t.textMuted }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.primarySofter}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <ChevronRight size={14} className={`flex-shrink-0 transition-transform ${sidebarCollapsed ? '' : 'rotate-180'}`} />
              {!sidebarCollapsed && <span>Réduire</span>}
            </button>
            <button onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] font-medium transition-colors"
              style={{ color: t.textMuted }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.dangerSoft; e.currentTarget.style.color = t.dangerText; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = t.textMuted; }}
            >
              <LogOut size={14} className="flex-shrink-0" />
              {!sidebarCollapsed && <span>Déconnexion</span>}
            </button>
          </div>
        </aside>

        {/* ============================================================== */}
        {/* MAIN                                                           */}
        {/* ============================================================== */}
        <div className="flex-1 flex flex-col min-w-0">

          {/* Status bar */}
          <div className="px-6 py-2.5 border-b sticky top-0 z-20" style={{ backgroundColor: t.bgStatusBar, borderColor: t.border }}>
            <SystemStatusBar stats={stats} onRefresh={() => loadData(true)} refreshing={refreshing} isDark={isDark} onToggleTheme={toggleTheme} />
          </div>

          <main className="flex-1 overflow-y-auto">
            <div className="max-w-[1440px] mx-auto p-6 overflow-hidden">

              {/* ──── OVERVIEW ──── */}
              {activeView === 'overview' && (
                <div className="space-y-6" style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                      <h2 className="text-lg font-bold tracking-tight mb-1" style={{ color: t.text }}>Tableau de bord</h2>
                      <p className="text-sm font-medium" style={{ color: t.textSecondary }}>
                        Supervision de {stats?.totalCompanies || 0} entreprises en temps réel
                      </p>
                    </div>
                    <button onClick={() => setShowCreateForm(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all active:scale-[0.98]"
                      style={{ backgroundColor: t.primarySoft, borderColor: t.primaryBorder, color: t.primaryText }}>
                      <Plus size={16} strokeWidth={2.5} />
                      Nouvelle entreprise
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <KPITile label="Entreprises" value={stats?.totalCompanies || 0} sub="enregistrées" />
                    <KPITile label="Actives" value={stats?.activeCompanies || 0}
                      sub={stats?.totalCompanies ? `${Math.round((stats.activeCompanies / stats.totalCompanies) * 100)}%` : undefined} />
                    <KPITile label="Utilisateurs" value={stats?.totalUsers || 0} sub="comptes" />
                    <KPITile label="Bloquées" value={stats?.blockedCompanies || 0} alert={stats?.blockedCompanies > 0} />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 overflow-hidden">
                    <div className="lg:col-span-2 min-w-0">
                      <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] mb-3" style={{ color: t.textMuted }}>Entreprises récentes</h3>
                      <MiniTable companies={companies} onViewAll={() => setActiveView('companies')} />
                    </div>
                    <div className="space-y-4 min-w-0">
                      <div>
                        <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] mb-3" style={{ color: t.textMuted }}>Résumé</h3>
                        <QuickStats stats={stats} />
                      </div>
                      <DistributionPanel stats={stats} />
                    </div>
                  </div>
                </div>
              )}

              {/* ──── COMPANIES ──── */}
              {activeView === 'companies' && (
                <div className="space-y-5" style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                      <h2 className="text-lg font-bold tracking-tight mb-1" style={{ color: t.text }}>Gestion des entreprises</h2>
                      <p className="text-sm font-medium" style={{ color: t.textSecondary }}>Administration des comptes, licences et accès</p>
                    </div>
                    <button onClick={() => setShowCreateForm(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all active:scale-[0.98]"
                      style={{ backgroundColor: t.primarySoft, borderColor: t.primaryBorder, color: t.primaryText }}>
                      <Plus size={16} strokeWidth={2.5} />
                      Ajouter
                    </button>
                  </div>

                  <QuickStats stats={stats} />

                  {/* Filters */}
                  <div className="flex flex-col lg:flex-row gap-3">
                    <div className="flex-1 relative group">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 transition-colors" size={16} style={{ color: t.textMuted }} />
                      <input type="text" placeholder="Rechercher par nom ou slug..."
                        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm font-medium border transition-all outline-none"
                        style={{ backgroundColor: t.bgInput, borderColor: t.border, color: t.text }}
                        onFocus={(e) => { e.target.style.backgroundColor = t.bgInputFocus; e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
                        onBlur={(e) => { e.target.style.backgroundColor = t.bgInput; e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                    <div className="relative">
                      <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        className="pl-4 pr-10 py-2.5 rounded-lg text-sm font-medium border appearance-none cursor-pointer min-w-[180px] outline-none transition-all"
                        style={{ backgroundColor: t.bgInput, borderColor: t.border, color: t.text }}
                        onFocus={(e) => { e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
                        onBlur={(e) => { e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
                      >
                        <option value="all">Tous les statuts</option>
                        <option value="active">Actives</option>
                        <option value="trial">En essai</option>
                        <option value="suspended">Suspendues</option>
                        <option value="blocked">Bloquées</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" size={14} style={{ color: t.textMuted }} />
                    </div>
                    <div className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border tabular-nums"
                      style={{ backgroundColor: t.bgCard, borderColor: t.border }}>
                      <span className="text-sm font-bold" style={{ color: t.text }}>{filteredCompanies.length}</span>
                      <span className="text-sm" style={{ color: t.textMuted }}>/</span>
                      <span className="text-sm" style={{ color: t.textSecondary }}>{companies.length}</span>
                    </div>
                  </div>

                  {/* List */}
                  <div className="rounded-xl border overflow-hidden" style={{ borderColor: t.border }}>
                    <div className="px-5 py-3 border-b" style={{ backgroundColor: t.bgTableHeader, borderColor: t.border }}>
                      <div className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: t.textMuted }}>
                        {filteredCompanies.length} entreprise{filteredCompanies.length !== 1 ? 's' : ''} trouvée{filteredCompanies.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <CompanyList companies={filteredCompanies} onCompanyUpdated={handleCompanyUpdated} onManageLicence={handleManageLicence} theme={theme} />
                    {filteredCompanies.length === 0 && (
                      <div className="py-16 text-center">
                        <Search size={32} className="mx-auto mb-3" style={{ color: t.textMuted }} />
                        <p className="text-sm font-medium" style={{ color: t.textSecondary }}>Aucun résultat</p>
                        <p className="text-xs mt-1" style={{ color: t.textMuted }}>Modifiez vos critères de recherche</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ──── FEATURES ──── */}
              {activeView === 'requetes' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <RequetesLentes />
                </div>
              )}

              {activeView === 'administrateurs' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <Administrateurs />
                </div>
              )}

              {activeView === 'conformite' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <Conformite />
                </div>
              )}

              {activeView === 'activite' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <Activite />
                </div>
              )}

              {activeView === 'actions' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <ActionsPrivilegiees />
                </div>
              )}

              {activeView === 'exploitation' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <Exploitation />
                </div>
              )}

              {activeView === 'connexions' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <LoginAttempts />
                </div>
              )}

              {activeView === 'features' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <FeatureFlags />
                </div>
              )}

              {activeView === 'login_config' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <LoginConfigManager />
                </div>
              )}

              {activeView === 'communications' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <CommunicationsAdmin />
                </div>
              )}

              {activeView === 'billing' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <BillingConfig />
                </div>
              )}

               {activeView === 'cron' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <CronManager />
                </div>
              )}

            
              {activeView === 'invoices' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <InvoicesManager />
                </div>
              )}


              {activeView === 'daniced-settings' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <DanicedSettings />
                </div>
              )}

               {activeView === 'prospection' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <ProspectionManager />
                </div>
              )}

                 {activeView === 'VersionManager' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <VersionManager />
                </div>
              )}

              


              
              {/* {activeView === 'contrats' && (
                <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                  <ContratManager
                    initialContratId={initialContratId}
                    onNavigate={(view) => setActiveView(view)}
                  />
                </div>
              )}


              {activeView === 'devis' && (
                <DevisManager onConvertirContrat={(data) => {
                  setInitialContratId(data.contrat_id || null);
                  setActiveView('contrats');
                }} />
              )}


             {/*activeView === 'prospects' && (
              <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                <ProspectsManager />
              </div>
            )}*/}


        
            {activeView === 'devis' && (
              <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                <DevisManager
                  initialProspectId={initialProspectId}
                  onConvertirContrat={(data) => {
                    setInitialContratId(data.contrat_id || null);
                    setActiveView('contrats');
                  }}
                />
              </div>
            )}


            {activeView === 'contrats' && (
            <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
              <ContratManager
                initialContratId={initialContratId}
                onNavigate={(view) => setActiveView(view)}
              />
            </div>
          )}
  

           {activeView === 'prospection_hub' && (
            <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
              <ProspectionHub
                onCreateDevis={(prospect) => {
                  setInitialProspectId(prospect?.id || null);
                  setActiveView('devis');
                }}
                onViewContrat={(contratId) => {
                  setInitialContratId(contratId || null);
                  setActiveView('contrats');
                }}
              />
            </div>
          )}

            {activeView === 'licences' && (
              <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                <LicenceManager />
              </div>
            )}

            {activeView === 'extensions' && (
              <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                <ExtensionsManager />
              </div>
            )}


             {activeView === 'licencesModule' && (
              <div style={{ animation: 'hpFadeIn 0.2s ease-out' }}>
                <LicenceModulesManager />
              </div>
            )}

            
              

            </div>
          </main>
        </div>

        {/* ============================================================== */}
        {/* MODALS                                                         */}
        {/* ============================================================== */}
        {showCreateForm && (
          <CompanyForm onClose={() => setShowCreateForm(false)} onSuccess={handleCompanyCreated} />
        )}
        {showLicenceModal && selectedCompany && (
          <LicenceManagementModal
            company={selectedCompany}
            onClose={() => { setShowLicenceModal(false); setSelectedCompany(null); }}
            onUpdate={handleLicenceUpdated}
          />
        )}

        {/* Global styles */}
        <style>{`
          @keyframes hpFadeIn {
            from { opacity: 0; transform: translateY(4px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          select option {
            background-color: ${t.bgSidebar};
            color: ${t.text};
          }
          ::-webkit-scrollbar { width: 6px; }
          ::-webkit-scrollbar-track { background: transparent; }
          ::-webkit-scrollbar-thumb { background: ${t.scrollThumb}; border-radius: 3px; }
          ::-webkit-scrollbar-thumb:hover { background: ${t.scrollThumbHover}; }
          ::placeholder { color: ${t.textMuted} !important; }
        `}</style>
      </div>
    </ThemeContext.Provider>
  );
};

export default SuperAdminDashboard;